import { useMutation, useQueryClient } from '@tanstack/react-query';

import { subscriptionsService, userService } from '@src/api/services';
import {
  ISubscriptionsApplyOfferRequest,
  ISubscriptionsInitPaymentData,
  ISubscriptionsInitPaymentRequest,
  ISubscriptionsRedeemOfferRequest,
} from '@src/api/types/subscriptions.types';
import {
  CURRENT_DOMAIN,
  GATEWAYS,
  NAVIGATION,
  PAYMENT_REDIRECT_STATUS,
  QUERY_KEYS,
  SEARCH_KEYS,
  TIMEOUT,
} from '@src/constants';
import {
  getDiscountOfferCode,
  setDiscountExpiresAt,
} from '@src/helpers/discount.helpers';
import { getCountryCodeBySubdomain } from '@src/helpers/host.helpers';
import { timeout } from '@src/helpers/timeout.helpers';
import { IPlanID } from '@src/interfaces/plans.interfaces';
import { IGateway } from '@src/interfaces/subscriptions.interfaces';

interface IUseSubscriptionInit {
  planID: IPlanID | undefined;
  onSuccess?: (data?: ISubscriptionsInitPaymentData) => void;
  onError?: () => void;
  onMutate?: () => void;
  currency: string;
  gateway?: IGateway;
  primerFroudNetID?: string;
}

export const useSubscriptionInit = ({
  currency,
  gateway,
  planID,
  onSuccess,
  onError,
  onMutate,
  primerFroudNetID,
}: IUseSubscriptionInit) => {
  return useMutation({
    mutationFn: async (
      requestData?: Partial<ISubscriptionsInitPaymentRequest>,
    ) => {
      if (!planID) return;

      const countryCode = getCountryCodeBySubdomain();

      const disableRedirect = requestData?.disableRedirect;
      if (disableRedirect) delete requestData?.disableRedirect;

      const params: ISubscriptionsInitPaymentRequest = {
        domain: countryCode,
        currency: currency.toUpperCase(),
        subscription_plan_id: planID,
        gateway,
        offer_code: getDiscountOfferCode(),
        ...requestData,
      };

      const isMollieMethod = params?.gateway === GATEWAYS.MOLLIE;

      if (isMollieMethod) {
        const response = await userService.getTemporaryToken();

        const newSuccessParams = new URLSearchParams({
          [SEARCH_KEYS.TOKEN]: response.data.data?.token || '',
          [SEARCH_KEYS.PLAN_ID]: planID.toString(),
        });

        params.success_url = `${CURRENT_DOMAIN}${NAVIGATION.CREATE_PASSWORD}?${newSuccessParams}`; // INFO: Redirect to the create password page from Mollie payment success

        const newCancelParams = new URLSearchParams({
          [SEARCH_KEYS.REDIRECT_STATUS]: PAYMENT_REDIRECT_STATUS.failed,
          [SEARCH_KEYS.PLAN_ID]: planID.toString(),
        });

        params.cancel_url = `${CURRENT_DOMAIN}${NAVIGATION.PAYMENT}?${newCancelParams}`; // INFO: Redirect to the current page from Mollie payment failed
      }

      if (primerFroudNetID) {
        params.metadata = {
          paypalClientMetadataId: primerFroudNetID,
        };
      }

      const action =
        !params.method && isMollieMethod
          ? subscriptionsService.quote
          : subscriptionsService.initPayment;

      const response = await action(params);
      const data = response.data.data;

      if (data?.redirect_url && !disableRedirect) {
        window.location.href = data.redirect_url;
        await timeout(TIMEOUT.TWO_SECONDS);
      }

      return data;
    },
    onSuccess,
    onError,
    onMutate,
  });
};

interface IUseMutationCancelSubscriptionOptions {
  onSuccess?: () => void;
  onMutate?: () => void;
}

export const useMutationCancelSubscription = ({
  onSuccess,
  onMutate,
}: IUseMutationCancelSubscriptionOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { minDuration: TIMEOUT.ONE_SECOND },
    mutationFn: async () => {
      const response = await subscriptionsService.cancelSubscription();
      return response.data.status === 'success';
    },
    onMutate,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.subscriptionsOffersEligibility],
      });
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.subscriptionsMe],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.authUserInfo],
      });
      onSuccess?.();
    },
  });
};

interface IUseMutationRenewSubscriptionOptions {
  onMutate?: () => void;
}

export const useMutationRenewSubscription = ({
  onMutate,
}: IUseMutationRenewSubscriptionOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { minDuration: TIMEOUT.ONE_SECOND },
    mutationFn: async () => {
      const response = await subscriptionsService.renewSubscription();
      return response.data.status === 'success';
    },
    onMutate,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.subscriptionsOffersEligibility],
      });
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.subscriptionsMe],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.authUserInfo],
      });
    },
  });
};

interface IUseMutationPauseToggleSubscription {
  isPaused?: boolean;
  invalidateEligibility?: boolean;
  onMutate?: () => void;
}

export const useMutationPauseToggleSubscription = ({
  isPaused,
  onMutate,
  invalidateEligibility = true,
}: IUseMutationPauseToggleSubscription = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { minDuration: TIMEOUT.ONE_SECOND },
    mutationFn: async () => {
      const action = isPaused
        ? subscriptionsService.unpause
        : subscriptionsService.pause;
      return await action();
    },
    onMutate,
    onSuccess: async () => {
      if (invalidateEligibility) {
        await queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.subscriptionsOffersEligibility],
        });
      }
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.subscriptionsMe],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.authUserInfo],
      });
    },
  });
};

interface IUseMutationApplyOfferSubscription {
  onMutate?: () => void;
  onError?: () => void;
}

export const useMutationApplyOfferSubscription = ({
  onMutate,
  onError,
}: IUseMutationApplyOfferSubscription) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { minDuration: TIMEOUT.ONE_SECOND },
    mutationFn: async (body: ISubscriptionsApplyOfferRequest) => {
      const response = await subscriptionsService.applyOffer(body);
      return response.data.success;
    },
    onMutate,
    onError,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.subscriptionsOffersEligibility],
      });
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.subscriptionsMe],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.authUserInfo],
      });
    },
  });
};

interface IUseMutationApplyFreePeriodSubscription {
  onMutate?: () => void;
  onError?: () => void;
}

export const useMutationApplyFreePeriodSubscription = ({
  onMutate,
  onError,
}: IUseMutationApplyFreePeriodSubscription) => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: { minDuration: TIMEOUT.ONE_SECOND },
    mutationFn: async () => {
      const response = await subscriptionsService.applyFreePeriod();
      return response.data.success;
    },
    onMutate,
    onError,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.subscriptionsOffersEligibility],
      });
      await queryClient.refetchQueries({
        queryKey: [QUERY_KEYS.subscriptionsMe],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.authUserInfo],
      });
    },
  });
};

export const useMutationRedeemOfferSubscription = () => {
  return useMutation({
    mutationFn: async (body: ISubscriptionsRedeemOfferRequest) => {
      const response = await subscriptionsService.redeemOffer(body);
      return response.data.data.expires_at;
    },
    onSuccess: (expiresAt, { offer_code }) =>
      setDiscountExpiresAt(offer_code, expiresAt),
  });
};
